import { useMemo, useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCollection } from "@/lib/hooks/useCollection";
import { call, CALLABLES } from "@/services/callables";
import { formatDateTime } from "@/lib/format";
import {
  ACTION_KEY,
  countUnanswered,
  isUrgent,
  nextStatuses,
  ordered,
  replyHref,
  SEGMENT_KEY,
  STATUS_KEY,
  STATUS_TONE,
  SUBJECT_KEY,
  type ContactStatus,
  type ContactSubmission,
} from "./lifecycle";

/**
 * What the website's contact form delivers.
 *
 * The form's page promises that a person answers within a working day. This is
 * where that person looks. The reply itself happens in their own mail client;
 * the console only records that it happened, so nobody answers twice and
 * nothing waits unnoticed.
 */
export function EnquiriesPage() {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();

  const { rows, loading, failed } = useCollection<ContactSubmission>("contactSubmissions");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);

  const list = useMemo(
    () => ordered(rows).filter((row) => showClosed || row.status !== "closed"),
    [rows, showClosed]
  );
  const unanswered = countUnanswered(rows);

  async function setStatus(row: ContactSubmission, status: ContactStatus) {
    if (!row.id) return;
    setBusyId(row.id);
    try {
      await call<{ id: string; status: ContactStatus }, { ok: true }>(CALLABLES.setContactStatus, {
        id: row.id,
        status,
      });
      success("enquiries.updated", { status: t(STATUS_KEY[status]) });
    } catch (error) {
      showError(error);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <SectionShell
      titleKey="enquiries.title"
      actions={
        <Button variant="ghost" onClick={() => setShowClosed((value) => !value)}>
          {t(showClosed ? "enquiries.hideClosed" : "enquiries.showClosed")}
        </Button>
      }
    >
      <p className="max-w-2xl text-sm text-ink-soft">{t("enquiries.intro")}</p>
      <p className="mt-2 text-sm text-ink-muted">{t("enquiries.unanswered", { count: unanswered })}</p>

      <section className="mt-8">
        {loading ? (
          <p className="text-sm text-ink-muted">{t("common.loading")}</p>
        ) : failed ? (
          <p role="alert" className="text-sm text-accent">
            {t("enquiries.failed")}
          </p>
        ) : list.length === 0 ? (
          <EmptyState titleKey="enquiries.empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t("enquiries.title")}</caption>
              <thead>
                <tr className="border-b border-paper-deep text-left text-ink-muted">
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("enquiries.column.received")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("enquiries.column.from")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("enquiries.column.about")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("enquiries.column.message")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("enquiries.column.status")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">
                    <span className="sr-only">{t("enquiries.column.actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr key={row.id ?? `${row.email}-${row.receivedAt}`} className="border-b border-paper-warm align-top last:border-0">
                    <td className="whitespace-nowrap px-3 py-3 text-ink-muted">
                      {formatDateTime(locale, row.receivedAt)}
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-ink">{row.name}</p>
                      <p>
                        <a className="text-ink-soft underline underline-offset-2" href={`mailto:${row.email}`}>
                          {row.email}
                        </a>
                      </p>
                      {row.institution ? <p className="text-ink-soft">{row.institution}</p> : null}
                      <p className="text-ink-muted">{t(SEGMENT_KEY[row.segment] ?? SEGMENT_KEY.other)}</p>
                    </td>
                    <td className="px-3 py-3 text-ink-soft">
                      <p>{t(SUBJECT_KEY[row.subject] ?? SUBJECT_KEY.general)}</p>
                      {isUrgent(row) ? <p className="mt-1 text-cue-amber">{t("enquiries.urgent")}</p> : null}
                    </td>
                    <td className="max-w-xl whitespace-pre-wrap px-3 py-3 text-ink">{row.message}</td>
                    <td className={`whitespace-nowrap px-3 py-3 ${STATUS_TONE[row.status] ?? STATUS_TONE.new}`}>
                      {t(STATUS_KEY[row.status] ?? STATUS_KEY.new)}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col items-start gap-2">
                        <a className="text-ink-soft underline underline-offset-2" href={replyHref(row)}>
                          {t("enquiries.reply")}
                        </a>
                        {row.id
                          ? nextStatuses(row.status).map((status) => (
                              <Button
                                key={status}
                                variant="secondary"
                                busy={busyId === row.id}
                                disabled={busyId !== null}
                                onClick={() => void setStatus(row, status)}
                              >
                                {t(ACTION_KEY[status])}
                              </Button>
                            ))
                          : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </SectionShell>
  );
}
